import { ref, computed } from 'vue'

export function useCvData(initialData = null) {
  // Data refs with default values
  const personalInfo = ref(initialData?.personalInfo || {
    fullName: '',
    email: '',
    phone: '',
    location: ''
  })

  const educationList = ref(initialData?.educationList || [{
    institution: '',
    location: '',
    startMonth: '',
    startYear: '',
    endMonth: '',
    endYear: '',
    programName: '',
    programType: '',
    gpa: '',
    bulletPoints: [''],
    isCurrent: false
  }])

  const experienceList = ref(initialData?.experienceList || [{
    jobTitle: '',
    company: '',
    startMonth: '',
    startYear: '',
    endMonth: '',
    endYear: '',
    location: '',
    companyDescription: '',
    bulletPoints: [''],
    isCurrent: false
  }])

  const activitiesList = ref(initialData?.activitiesList || [{
    organization: '',
    role: '',
    startMonth: '',
    startYear: '',
    endMonth: '',
    endYear: '',
    bulletPoints: [''],
    isCurrent: false
  }])

  const skillCategories = ref(initialData?.skillCategories || [{
    name: '',
    content: ''
  }])

  // Check if form is essentially empty
  const isFormEmpty = computed(() => {
    // Check if personal info has meaningful content
    const hasPersonalInfo = personalInfo.value.fullName?.trim() || 
                           personalInfo.value.email?.trim() || 
                           personalInfo.value.phone?.trim() || 
                           personalInfo.value.location?.trim()

    // Check if education has meaningful content
    const hasEducation = educationList.value.some(edu => 
      edu.institution?.trim() || edu.programName?.trim() || edu.programType?.trim()
    )

    // Check if experience has meaningful content
    const hasExperience = experienceList.value.some(exp => 
      exp.jobTitle?.trim() || exp.company?.trim()
    )

    // Check if activities has meaningful content
    const hasActivities = activitiesList.value.some(act => 
      act.organization?.trim() || act.role?.trim()
    )

    // Check if skill categories has meaningful content
    const hasSkillCategories = skillCategories.value.some(cat => 
      cat.name?.trim() || cat.content?.trim()
    )

    return !hasPersonalInfo && !hasEducation && !hasExperience && !hasActivities && !hasSkillCategories
  })

  // Convert camelCase to snake_case for Rails
  const toSnakeCase = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(toSnakeCase)
    } else if (obj !== null && typeof obj === 'object') {
      const result = {}
      for (const [key, value] of Object.entries(obj)) {
        const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase()
        result[snakeKey] = toSnakeCase(value)
      }
      return result
    }
    return obj
  }

  // Get CV data in the format expected by the backend
  const getCvData = () => ({
    personalInfo: personalInfo.value,
    educationList: educationList.value,
    experienceList: experienceList.value,
    activitiesList: activitiesList.value,
    skillCategories: skillCategories.value
  })

  // Get CV data in snake_case format for Rails
  const getCvDataForBackend = () => toSnakeCase(getCvData())

  // Set CV data from external source (like wizard)
  const setCvData = (data) => {
    if (data.personalInfo) personalInfo.value = data.personalInfo
    if (data.educationList) educationList.value = data.educationList
    if (data.experienceList) experienceList.value = data.experienceList
    if (data.activitiesList) activitiesList.value = data.activitiesList
    if (data.skillCategories) skillCategories.value = data.skillCategories
  }

  // Clear all CV data
  const clearCvData = () => {
    personalInfo.value = {
      fullName: '',
      email: '',
      phone: '',
      location: ''
    }
    educationList.value = [{
      institution: '',
      location: '',
      startMonth: '',
      startYear: '',
      endMonth: '',
      endYear: '',
      programName: '',
      programType: '',
      gpa: '',
      bulletPoints: [''],
      isCurrent: false
    }]
    experienceList.value = [{
      jobTitle: '',
      company: '',
      startMonth: '',
      startYear: '',
      endMonth: '',
      endYear: '',
      location: '',
      companyDescription: '',
      bulletPoints: [''],
      isCurrent: false
    }]
    activitiesList.value = [{
      organization: '',
      role: '',
      startMonth: '',
      startYear: '',
      endMonth: '',
      endYear: '',
      bulletPoints: [''],
      isCurrent: false
    }]
    skillCategories.value = [{
      name: '',
      content: ''
    }]
  }

  return {
    personalInfo,
    educationList,
    experienceList,
    activitiesList,
    skillCategories,
    isFormEmpty,
    getCvData,
    getCvDataForBackend,
    setCvData,
    clearCvData,
    toSnakeCase
  }
}